import DashboardLayout from '@/components/DashboardLayout';
import { useForm } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Users, Package, CreditCard, Percent, CalendarIcon, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { router } from '@inertiajs/react';

interface Student {
  id: number;
  name: string;
  email: string;
}

interface LessonPackage {
  id: number;
  name: string;
  description: string;
  lessons_count: number;
  price: string;
  formatted_price: string;
  price_per_lesson: number;
}

interface Payment {
  id: number;
  status: string;
  payment_method: string;
  amount: number;
}

interface Subscription {
  id: number;
  student_id: number;
  lesson_package_id: number;
  discount_percentage: number;
  final_price: string;
  booking_deadline?: string;
  lessons_remaining: number;
  purchased_at: string;
  student: Student;
  lesson_package: LessonPackage;
  payment?: Payment;
}

interface Props {
  subscription: Subscription;
  students: Student[];
  lesson_packages: LessonPackage[];
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Dinheiro' },
  { value: 'credit_card', label: 'Cartão de Crédito' },
  { value: 'debit_card', label: 'Cartão de Débito' },
  { value: 'pix', label: 'PIX' },
  { value: 'bank_transfer', label: 'Transferência Bancária' },
];

const PAYMENT_STATUS = [
  { value: 'paid', label: 'Pago', variant: 'default' as const, icon: CheckCircle },
  { value: 'pending', label: 'Pendente', variant: 'outline' as const, icon: XCircle },
];

export default function Edit({ subscription, students, lesson_packages }: Props) {
  const [selectedPackage, setSelectedPackage] = useState<LessonPackage | null>(subscription.lesson_package);
  const [bookingDeadline, setBookingDeadline] = useState<Date | undefined>(
    subscription.booking_deadline ? new Date(subscription.booking_deadline) : undefined
  );
  
  const { data, setData, patch, processing, errors } = useForm({
    student_id: subscription.student_id.toString(),
    lesson_package_id: subscription.lesson_package_id.toString(),
    payment_method: subscription.payment?.payment_method || 'cash',
    payment_status: subscription.payment?.status || 'pending',
    discount_percentage: subscription.discount_percentage.toString(),
    booking_deadline: subscription.booking_deadline || '',
    lessons_remaining: subscription.lessons_remaining.toString(),
  });

  // Calcula o preço final com desconto
  const calculatedPrice = useMemo(() => {
    if (!selectedPackage) return null;
    
    const originalPrice = parseFloat(selectedPackage.price);
    const discount = parseFloat(data.discount_percentage) || 0;
    const discountAmount = (originalPrice * discount) / 100;
    const finalPrice = originalPrice - discountAmount;
    
    return {
      original: originalPrice,
      discount: discount,
      discountAmount: discountAmount,
      final: finalPrice,
    };
  }, [selectedPackage, data.discount_percentage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch(`/student_subscriptions/${subscription.id}`);
  };

  const handlePackageChange = (packageId: string) => {
    const pkg = lesson_packages.find(p => p.id.toString() === packageId) || null;
    setSelectedPackage(pkg);
    setData(data => ({
      ...data,
      lesson_package_id: packageId,
      // Ao trocar o plano (correção de cadastro), sincroniza as aulas restantes
      // com o total do novo pacote. O admin ainda pode ajustar manualmente.
      lessons_remaining: pkg ? pkg.lessons_count.toString() : data.lessons_remaining,
    }));
  };

  const selectedStudent = students.find(s => s.id.toString() === data.student_id);
  const paymentStatusInfo = PAYMENT_STATUS.find(s => s.value === data.payment_status);

  return (
    <DashboardLayout>
    <div className="p-8 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>Editar Assinatura #{subscription.id}</CardTitle>
                <CardDescription>
                  Criada em {format(new Date(subscription.purchased_at), 'dd/MM/yyyy', { locale: ptBR })}
                </CardDescription>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => router.visit('/student_subscriptions')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Aulas Restantes (editável pelo admin) */}
            <div className="space-y-2">
              <Label htmlFor="lessons_remaining">
                <Package className="w-4 h-4 inline mr-1" />
                Aulas Restantes *
              </Label>
              <Input
                id="lessons_remaining"
                type="number"
                min={0}
                value={data.lessons_remaining}
                onChange={(e) => setData('lessons_remaining', e.target.value)}
                className="max-w-[140px]"
              />
              <p className="text-xs text-muted-foreground">
                Pacote original: {subscription.lesson_package?.lessons_count ?? '—'} aulas. Você pode definir um valor maior para dar bônus ao aluno.
              </p>
              {errors.lessons_remaining && (
                <p className="text-sm text-red-500">{errors.lessons_remaining}</p>
              )}
            </div>

            {/* Aluno */}
            <div className="space-y-2">
              <Label htmlFor="student_id">
                Aluno *
              </Label>
              <Select
                value={data.student_id}
                onValueChange={(value) => setData('student_id', value)}
                disabled
              >
                <SelectTrigger id="student_id" className="bg-gray-50 dark:bg-gray-900">
                  <SelectValue placeholder="Selecione o aluno" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((student) => (
                    <SelectItem key={student.id} value={student.id.toString()}>
                      <div>
                        <div className="font-medium">{student.name}</div>
                        <div className="text-xs text-muted-foreground">{student.email}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedStudent && (
                <div className="text-xs text-muted-foreground flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-900 rounded-md">
                  <Users className="w-3 h-3" />
                  {selectedStudent.email}
                </div>
              )}
              {errors.student_id && <p className="text-sm text-red-500">{errors.student_id}</p>}
            </div>

            {/* Pacote de Aulas */}
            <div className="space-y-2">
              <Label htmlFor="lesson_package_id">
                Pacote de Aulas *
              </Label>
              <Select
                value={data.lesson_package_id}
                onValueChange={handlePackageChange}
              >
                <SelectTrigger id="lesson_package_id">
                  <SelectValue placeholder="Selecione o pacote" />
                </SelectTrigger>
                <SelectContent>
                  {lesson_packages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id.toString()}>
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <div className="font-medium">{pkg.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {pkg.lessons_count} aulas • R$ {pkg.price_per_lesson.toFixed(2)}/aula
                          </div>
                        </div>
                        <div className="ml-4 text-sm font-semibold text-primary">
                          {pkg.formatted_price}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.lesson_package_id && <p className="text-sm text-red-500">{errors.lesson_package_id}</p>}
            </div>

            {/* Desconto */}
            <div className="space-y-2">
              <Label htmlFor="discount_percentage">
                <Percent className="w-4 h-4 inline mr-1" />
                Desconto (%)
              </Label>
              <Input
                id="discount_percentage"
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="0"
                value={data.discount_percentage}
                onChange={(e) => setData('discount_percentage', e.target.value)}
              />
              {errors.discount_percentage && <p className="text-sm text-red-500">{errors.discount_percentage}</p>}
            </div>

            {/* Prazo para Agendar */}
            <div className="space-y-2">
              <Label>
                <CalendarIcon className="w-4 h-4 inline mr-1" />
                Prazo Final para Agendar Aulas
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {bookingDeadline ? (
                      format(bookingDeadline, 'dd/MM/yyyy', { locale: ptBR })
                    ) : (
                      <span>Sem prazo limite</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={bookingDeadline}
                    onSelect={(date) => {
                      setBookingDeadline(date);
                      setData('booking_deadline', date ? format(date, 'yyyy-MM-dd') : '');
                    }}
                    locale={ptBR}
                    disabled={(date) => date < new Date()}
                  />
                  {bookingDeadline && (
                    <div className="p-3 border-t">
                      <Button
                        variant="ghost"
                        className="w-full text-xs"
                        onClick={() => {
                          setBookingDeadline(undefined);
                          setData('booking_deadline', '');
                        }}
                      >
                        Remover Prazo
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>

            {/* Pagamento */}
            <div className="space-y-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-900">
              <h3 className="font-semibold flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Informações de Pagamento
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Método de Pagamento */}
                <div className="space-y-2">
                  <Label htmlFor="payment_method">
                    Método de Pagamento *
                  </Label>
                  <Select
                    value={data.payment_method}
                    onValueChange={(value) => setData('payment_method', value)}
                  >
                    <SelectTrigger id="payment_method">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((method) => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status do Pagamento */}
                <div className="space-y-2">
                  <Label htmlFor="payment_status">
                    Status do Pagamento *
                  </Label>
                  <Select
                    value={data.payment_status}
                    onValueChange={(value) => setData('payment_status', value)}
                  >
                    <SelectTrigger id="payment_status">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUS.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          <div className="flex items-center gap-2">
                            {status.icon && <status.icon className="w-4 h-4" />}
                            {status.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Status Badge */}
              {paymentStatusInfo && (
                <div className="pt-2">
                  <Badge variant={paymentStatusInfo.variant}>
                    {paymentStatusInfo.icon && <paymentStatusInfo.icon className="w-3 h-3 mr-1" />}
                    Status: {paymentStatusInfo.label}
                  </Badge>
                </div>
              )}
            </div>

            {/* Resumo do Valor */}
            {calculatedPrice && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Resumo do Valor</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Valor Original:</span>
                    <span>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(calculatedPrice.original)}
                    </span>
                  </div>
                  
                  {calculatedPrice.discount > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-orange-600">
                        <span>Desconto ({calculatedPrice.discount}%):</span>
                        <span>
                          -{new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(calculatedPrice.discountAmount)}
                        </span>
                      </div>
                      <div className="border-t pt-2" />
                    </>
                  )}
                  
                  <div className="flex justify-between text-lg font-bold">
                    <span>Valor Final:</span>
                    <span className="text-green-600">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(calculatedPrice.final)}
                    </span>
                  </div>
                  
                  {selectedPackage && (
                    <div className="text-xs text-muted-foreground pt-2 border-t">
                      {selectedPackage.lessons_count} aulas • {' '}
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(calculatedPrice.final / selectedPackage.lessons_count)}/aula
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => router.visit('/student_subscriptions')}
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1"
                disabled={processing}
              >
                {processing ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
